import React, { useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import {
  SEMANTIC_COLOR_GROUPS,
  SemanticColorToken,
} from '../../../design-system/tokens/semantic-colors';

/**
 * Semantic › Semantic Colors — traslado del frame "Semantic Colors" de Figma
 * (node 23:555). Tabla Name | Propósito | Color: cada fila es un token semántico
 * que apunta a un primitivo (chip con el color + la referencia). Los grupos
 * (Background / Text / Icon / Stroke) se separan en tabs, mostrando el grupo activo.
 *
 * Datos desde la fuente de verdad `SEMANTIC_COLOR_GROUPS`.
 */

/** Chip del color: pill con dot (28px) + referencia al primitivo. */
const ColorChip: React.FC<{ hex: string; label: string }> = ({ hex, label }) => (
  // gap/px/py usan los tokens de spacing del DS (space150/100/75), igual que el Figma.
  <span className="inline-flex items-center gap-space150 rounded-full border border-[#e5e5e5] px-space100 py-space75">
    <span
      className="size-7 shrink-0 rounded-full border border-black/10"
      style={{ backgroundColor: hex }}
    />
    <span className="whitespace-nowrap text-[15px] text-[#484848]">{label}</span>
  </span>
);

/** Una fila de la tabla. */
const Row: React.FC<{ token: SemanticColorToken }> = ({ token }) => (
  <div className="flex items-center gap-8 px-4 py-2.5">
    <div className="w-[250px] shrink-0 text-[16px] text-[#484848]">{token.name}</div>
    <div className="min-w-0 flex-1 text-[16px] leading-[22px] text-[#484848]">{token.purpose}</div>
    <div className="w-[240px] shrink-0">
      <ColorChip hex={token.hex} label={token.ref} />
    </div>
  </div>
);

export const DSSemanticColorsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>(SEMANTIC_COLOR_GROUPS[0].name);
  const activeGroup =
    SEMANTIC_COLOR_GROUPS.find((g) => g.name === activeTab) ?? SEMANTIC_COLOR_GROUPS[0];

  return (
    <div className="px-10 py-12">
      <div className="mx-auto flex w-full max-w-[800px] flex-col gap-10">
        <PageHeader
          title="Semantic Colors"
          description="Los tokens semánticos conectan la paleta primitiva con su uso real en la interfaz: cada uno describe una intención —el fondo de una superficie, el color de un texto o un estado— en lugar de un valor crudo. Así se mantiene la coherencia visual y alcanza con cambiar el primitivo para recolorear todo el sistema."
        />

        {/* Tabs por grupo + tabla del grupo activo */}
        <div className="flex flex-col gap-8">
          <div
            role="tablist"
            aria-label="Grupos de color semántico"
            className="flex gap-6 border-b border-neutral-200"
          >
            {SEMANTIC_COLOR_GROUPS.map((group) => {
              const active = group.name === activeTab;
              return (
                <button
                  key={group.name}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(group.name)}
                  className={`-mb-px cursor-pointer border-b-2 px-1 pb-3 text-[15px] transition-colors ${
                    active
                      ? 'border-brand-primary font-semibold text-brand-primary'
                      : 'border-transparent font-medium text-neutral-500 hover:text-neutral-900'
                  }`}
                >
                  {group.name}
                </button>
              );
            })}
          </div>

          <div className="w-full">
            {/* Head — mismo estilo que la tabla de Spacing (sin fondo, sólo borde inferior) */}
            <div className="flex items-center gap-8 border-b border-neutral-300 px-4 py-3 text-[14px] font-semibold text-neutral-900">
              <div className="w-[250px] shrink-0">Name</div>
              <div className="min-w-0 flex-1">Propósito</div>
              <div className="w-[240px] shrink-0">Color</div>
            </div>

            {activeGroup.tokens.map((token) => (
              <Row key={token.name} token={token} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
